import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import React from "react";
import { Home, ClipboardList, Store, ShieldCheck, User, Wrench, Bell, Heart, LifeBuoy, Menu, X, MapPin, Search, Trophy, LogOut } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api } from "../api";
import { useSiteSettings } from "../context/SiteSettingsContext";
import CustomerWelcomeCard, { CUSTOMER_WELCOME_KEY } from "./CustomerWelcomeCard.jsx";
import ConnectionStatus from "./ConnectionStatus.jsx";
import InstallAppPrompt from "./InstallAppPrompt.jsx";

/* removed invalid embedded legacy logo */

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const { settings, previewMode } = useSiteSettings();
  const navSettings = settings?.navigation || {};
  // Brand identity is pinned to the owner-approved asset.
  // CMS carousel/media changes must never replace the site logo.
  const headerLogoSrc = "/arctic-plate-logo.webp";
  const footer = settings?.footer || {};
  const isAdminPath = location.pathname.startsWith("/admin");
  const isMarketplaceHome = location.pathname === "/";

  const role = user?.role;
  const previewAsGuest = Boolean(previewMode && role === "admin");
  const navRole = previewAsGuest ? null : role;
  // Vendor routes render their own SellerBottomNav through VendorLayout.
  // Suppress the global mobile nav for vendors so two fixed nav bars never
  // stack and Account does not route to the customer account screen.
  const hideGlobalMobileNav = navRole === "vendor";
  const hideMobileNavForSiteEditor = location.pathname === "/admin/site-editor";
  const showGlobalMobileNav = Boolean(user && !previewAsGuest && !hideGlobalMobileNav && !hideMobileNavForSiteEditor);
  const canUseBuyerCart = user === false || navRole === "customer" || previewAsGuest;
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [showCustomerWelcome, setShowCustomerWelcome] = React.useState(false);

  React.useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (user?.role !== "customer") {
      setShowCustomerWelcome(false);
      return;
    }
    try {
      setShowCustomerWelcome(window.sessionStorage.getItem(CUSTOMER_WELCOME_KEY) === "1");
    } catch {
      setShowCustomerWelcome(false);
    }
  }, [location.pathname, user]);

  // Admin-only unread badge — polls every 60s, stops on unmount / role change.
  const [unread, setUnread] = React.useState(0);
  React.useEffect(() => {
    if (role !== "admin") { setUnread(0); return; }
    let cancelled = false;
    let inflight = false;
    const fetchUnread = async () => {
      if (inflight || cancelled) return;
      inflight = true;
      try {
        const { data } = await api.get("/admin/notifications/unread-count");
        if (!cancelled) setUnread(Number(data?.unread_count) || 0);
      } catch (_e) { /* silent — never break the nav */ }
      finally { inflight = false; }
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 60000);
    const onVis = () => { if (!document.hidden) fetchUnread(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [role]);

  const [customerUnread, setCustomerUnread] = React.useState(0);
  React.useEffect(() => {
    if (role !== "customer") { setCustomerUnread(0); return undefined; }
    let cancelled = false;
    let inflight = false;
    const fetchUnread = async () => {
      if (cancelled || inflight) return;
      inflight = true;
      try {
        const [retentionResult, notificationResult] = await Promise.allSettled([
          api.get("/retention/inbox"),
          api.get("/notifications/me"),
        ]);
        const retentionRows = retentionResult.status === "fulfilled" && Array.isArray(retentionResult.value?.data)
          ? retentionResult.value.data
          : [];
        const systemRows = notificationResult.status === "fulfilled" && Array.isArray(notificationResult.value?.data?.items)
          ? notificationResult.value.data.items
          : [];
        const unreadRows = [
          ...retentionRows.map((row) => ({
            ...row,
            _message: row?.body || row?.message || "",
          })),
          ...systemRows.map((row) => ({
            ...row,
            _message: row?.message || row?.body || "",
          })),
        ].filter((row) => !row?.read_at);
        const seen = new Set();
        const uniqueUnread = unreadRows.filter((row) => {
          const key = [
            String(row?.title || "").trim().toLowerCase(),
            String(row?._message || "").trim().toLowerCase(),
            String(row?.href || "").trim(),
          ].join("|");
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        if (!cancelled) setCustomerUnread(uniqueUnread.length);
      } catch (_error) {
        // Notification count is decorative; never interrupt navigation.
      } finally {
        inflight = false;
      }
    };
    fetchUnread();
    const interval = window.setInterval(fetchUnread, 60000);
    const onVisible = () => { if (!document.hidden) fetchUnread(); };
    const onUpdated = () => {
      fetchUnread();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("ap:customer-alerts-updated", onUpdated);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("ap:customer-alerts-updated", onUpdated);
    };
  }, [role]);

  // VENDOR MODE IS STRICTLY ISOLATED — vendors never see the customer
  // marketplace from inside their dashboard shell. The Browse / "/" tab is
  // omitted for the vendor role so the only tabs are Business + Account.
  // App.js also redirects vendors away from "/" so a direct URL hit doesn't
  // leak the public feed either.
  const tabs = [
    // Admin, customer, guest: Browse marketplace. Vendors and admins
    // both stay OUT of the public marketplace tab — admins have their
    // own operating surface (Admin/HYDRA/Notifications) and vendors
    // never glance the consumer feed.
    (navRole === "customer" || !navRole)
      ? { to: "/", label: "Home", icon: Home, testid: "tab-home" }
      : null,
    navRole === "customer" || !navRole
      ? { to: "/orders", label: "Orders", icon: ClipboardList, testid: "tab-orders" }
      : null,
    navRole === "customer"
      ? { to: "/saved", label: "Saved", icon: Heart, testid: "tab-saved", activePaths: ["/saved", "/following", "/alerts"] }
      : null,
    navRole === "vendor" && { to: "/vendor/storefront", label: "Business", icon: Store, testid: "tab-vendor" },
    navRole === "admin" && { to: "/admin", label: "Admin", icon: ShieldCheck, testid: "tab-admin" },
    navRole === "admin" && { to: "/admin/arcticops", label: "HYDRA", icon: Wrench, testid: "tab-hydra" },
    navRole === "admin" && { to: "/admin/notifications", label: "Alerts", icon: Bell, testid: "tab-notifications", badge: unread },
    { to: "/account", label: "Account", icon: User, testid: "tab-account" },
  ].filter(Boolean);

  // Header logo click — customers/guests/admins go to the marketplace home,
  // vendors go to their business dashboard so they never glance the public feed.
  const logoTarget = navRole === "vendor" ? "/vendor/storefront" : "/";

  const goMarketplaceSection = (sectionId) => {
    setMobileMenuOpen(false);
    navigate("/");
    window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const goMobileRoute = (route) => {
    setMobileMenuOpen(false);
    navigate(route);
  };

  return (
    <div className={`${isMarketplaceHome ? "ap-shell ap-shell-marketplace" : "ap-shell"} min-w-0 max-w-full overflow-x-hidden`}>
      {/* Header — matches the owner-approved marketplace mockup. */}
      <header className={`sticky top-0 z-40 flex items-center justify-between gap-2 border-b px-5 py-3 backdrop-blur sm:px-8 lg:px-12 ${isMarketplaceHome ? "border-[#e7e5e4] bg-white/[0.98] text-[#0b2340]" : "border-[#e5dccf] bg-[#fffdf8]/97"}`}>
        <button
          data-testid="header-logo"
          onClick={() => navigate(logoTarget)}
          className="flex items-center gap-2 shrink-0"
        >
          <img
            src={headerLogoSrc}
            alt=""
            className={`h-14 w-14 rounded-full border bg-white object-contain p-1 shadow-sm sm:h-16 sm:w-16 lg:h-[72px] lg:w-[72px] ${isMarketplaceHome ? "border-[#d8d4ce] ring-2 ring-[#0b2340]/5" : "border-[#d7c6ae]"}`}
            data-testid="header-logo-img"
            aria-hidden="true"
            decoding="async"
            fetchPriority="high"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = "/arctic-plate-logo.webp";
            }}
          />
          
        </button>

        {user && !previewAsGuest && (
          <nav aria-label="Primary" className="hidden md:flex flex-1 items-center justify-center gap-1 px-4">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                end={t.to === "/"}
                aria-current={(location.pathname === t.to || t.activePaths?.includes(location.pathname)) ? "page" : undefined}
                className={({ isActive }) =>
                  `relative inline-flex min-h-[44px] items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                    (isActive || t.activePaths?.includes(location.pathname))
                      ? (isMarketplaceHome ? "bg-[#0b2340]/5 text-[#0b2340]" : "bg-[#FFF3E8] text-[var(--ap-primary)]")
                      : (isMarketplaceHome ? "text-[#0b2340]/85 hover:bg-[#0b2340]/5 hover:text-[#0b2340]" : "text-[var(--ap-ink-soft)] hover:bg-white hover:text-[var(--ap-ink)]")
                  }`
                }
              >
                <t.icon size={17} strokeWidth={1.9} />
                <span>{t.label}</span>
                {t.badge > 0 && (
                  <span className="ml-0.5 min-w-[18px] rounded-full bg-[var(--ap-primary)] px-1.5 py-0.5 text-center text-[10px] font-bold text-white">
                    {t.badge > 99 ? "99+" : t.badge}
                  </span>
                )}
              </NavLink>
            ))}
            {navRole === "customer" && (navSettings.optional_links || []).map((item) => (
              <Link key={item.route} to={item.route} className={`hidden xl:inline-flex min-h-[44px] items-center rounded-full px-3 py-2 text-sm font-semibold ${isMarketplaceHome ? "text-[#0b2340]/85 hover:bg-[#0b2340]/5 hover:text-[#0b2340]" : "text-[var(--ap-ink-soft)] hover:bg-white hover:text-[var(--ap-primary)]"}`}>
                {item.label}
              </Link>
            ))}
          </nav>
        )}

        {(!user || previewAsGuest) && (
          <nav className="hidden lg:flex flex-1 items-center justify-center gap-1 px-4" aria-label="Public">
            <button type="button" onClick={() => navigate("/")} data-testid="header-home" className={`min-h-[44px] border-b-2 border-[#f97316] px-4 py-2 text-sm font-bold ${isMarketplaceHome ? "text-[#b04600]" : "text-[#0b2340]"}`}>Home</button>
            <button type="button" onClick={() => goMarketplaceSection("kitchens")} data-testid="header-local-kitchens" className={`min-h-[44px] px-4 py-2 text-sm font-semibold ${isMarketplaceHome ? "text-[#0b2340] hover:text-[#b04600]" : "text-[#0b2340] hover:text-[#b97809]"}`}>Businesses</button>
            <button type="button" onClick={() => goMarketplaceSection("categories")} data-testid="header-categories" className={`min-h-[44px] px-4 py-2 text-sm font-semibold ${isMarketplaceHome ? "text-[#0b2340] hover:text-[#b04600]" : "text-[#0b2340] hover:text-[#b97809]"}`}>Cuisines</button>
            <button type="button" onClick={() => navigate("/about")} data-testid="header-about" className={`min-h-[44px] px-4 py-2 text-sm font-semibold ${isMarketplaceHome ? "text-[#0b2340] hover:text-[#b04600]" : "text-[#0b2340] hover:text-[#b97809]"}`}>About</button>
            <button type="button" onClick={() => navigate("/sell-food")} data-testid="header-sell-food" className={`min-h-[44px] px-4 py-2 text-sm font-semibold ${isMarketplaceHome ? "text-[#0b2340] hover:text-[#b04600]" : "text-[#0b2340] hover:text-[#b97809]"}`}>For Businesses</button>
          </nav>
        )}

        <div className="flex items-center gap-1.5">
          {(!user || previewAsGuest) && (
            <>
              <div className={`hidden xl:inline-flex min-h-[40px] items-center gap-1.5 px-2 text-xs font-semibold ${isMarketplaceHome ? "text-[#0b2340]" : "text-[#0b2340]"}`} aria-label="Marketplace region">
                <MapPin size={15} className={isMarketplaceHome ? "text-[#b04600]" : "text-[#0b2340]"} /> Anchorage, AK
              </div>
              <button type="button" onClick={() => goMarketplaceSection("available-now")} className={`hidden h-10 w-10 items-center justify-center rounded-full lg:inline-flex ${isMarketplaceHome ? "text-[#0b2340] hover:bg-[#0b2340]/5" : "text-[#0b2340] hover:bg-[#f7f0e5]"}`} aria-label="Search food">
                <Search size={18} />
              </button>
              <button
                data-testid="header-login"
                onClick={() => navigate("/login")}
                className={`hidden lg:inline-flex min-h-[40px] items-center rounded-md border px-4 text-xs font-bold ${isMarketplaceHome ? "border-[#cfc5b6] bg-white text-[#0b2340] hover:bg-[#f7f7f5]" : "border-[#cfc5b6] bg-white text-[#0b2340]"}`}
              >
                Sign In
              </button>
              <button
                data-testid="header-signup"
                onClick={() => navigate("/register")}
                className="hidden lg:inline-flex min-h-[40px] items-center rounded-md bg-[#e85b0b] px-4 text-xs font-bold text-white shadow-sm hover:bg-[#cf4e08]"
              >
                Sign Up
              </button>
            </>
          )}
          {(!user || previewAsGuest) && (
            <button
              type="button"
              data-testid="header-mobile-menu"
              onClick={() => setMobileMenuOpen((open) => !open)}
              className={`lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border ${isMarketplaceHome ? "border-[#d8d4ce] bg-white text-[#0b2340]" : "border-[var(--ap-border)] bg-white text-[var(--ap-ink)]"}`}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="customer-mobile-menu"
            >
              {mobileMenuOpen ? <X size={19} /> : <Menu size={20} />}
            </button>
          )}
          {navRole === "customer" && (
            <button
              type="button"
              data-testid="header-customer-alerts"
              onClick={() => navigate("/alerts")}
              className={`relative flex h-10 w-10 items-center justify-center rounded-full border ${isMarketplaceHome ? "border-[#d8d4ce] bg-white text-[#0b2340]" : "border-[var(--ap-border)] bg-white text-[var(--ap-ink)]"}`}
              aria-label={customerUnread > 0 ? `Notifications, ${customerUnread} unread` : "Notifications"}
            >
              <Bell size={17} aria-hidden="true" />
              {customerUnread > 0 && (
                <span className="absolute -right-1 -top-1 min-w-[18px] rounded-full bg-[var(--ap-primary)] px-1.5 py-0.5 text-center text-[9px] font-bold text-white">
                  {customerUnread > 99 ? "99+" : customerUnread}
                </span>
              )}
            </button>
          )}
          {/* Buyer cart controls exist only for guests and customers. */}
          {canUseBuyerCart && (
            <button
              data-testid="header-cart"
              onClick={() => navigate("/cart")}
              className={`relative w-9 h-9 rounded-full border flex items-center justify-center ${isMarketplaceHome ? "border-[#d8d4ce] bg-white text-[#0b2340]" : "border-[var(--ap-border)] bg-white"}`}
              aria-label="Cart"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle><circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {count > 0 && (
                <span
                  data-testid="cart-badge"
                  className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-[var(--ap-primary)] text-white text-[10px] font-bold flex items-center justify-center px-1"
                >
                  {count}
                </span>
              )}
            </button>
          )}
        </div>
      </header>

      {mobileMenuOpen && (!user || previewAsGuest) && (
        <div className={`fixed inset-0 z-50 ${(!user || previewAsGuest) ? "lg:hidden" : "md:hidden"}`} data-testid="customer-mobile-menu-shell">
          <button
            type="button"
            className="absolute inset-0 bg-[#17212b]/45 backdrop-blur-[2px]"
            aria-label="Close menu"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside
            id="customer-mobile-menu"
            className="absolute right-0 top-0 flex h-full w-[86%] max-w-sm flex-col bg-[#fffaf4] shadow-[-20px_0_60px_-30px_rgba(23,33,43,0.55)]"
            aria-label="Customer navigation"
          >
            <div className="relative flex min-h-36 items-end justify-between overflow-hidden border-b border-[#243c58] bg-[#10233f] px-5 py-5 text-white">
              <img src="/customer-final/bread-pudding.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#10233f] via-[#10233f]/90 to-[#10233f]/30" aria-hidden="true" />
              <div className="relative">
                <div className="font-serif text-2xl font-semibold text-white">Arctic Plate</div>
                <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#ffb372]">Find food faster</div>
              </div>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/50 bg-white/90 text-[#10233f]"
                aria-label="Close menu"
              >
                <X size={19} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto px-4 py-4" aria-label="Mobile customer menu">
              <div className="px-4 pb-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#b04600]">Explore</div>
              <button type="button" onClick={() => goMarketplaceSection("available-now")} className="w-full rounded-xl px-4 py-2.5 text-left text-sm font-semibold text-[#10233f] hover:bg-white">
                Browse Food
              </button>
              <button type="button" onClick={() => goMarketplaceSection("categories")} className="w-full rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                Categories
              </button>
              <button type="button" onClick={() => goMarketplaceSection("kitchens")} className="w-full rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                Businesses
              </button>

              {navRole === "customer" ? (
                <>
                  <div className="my-3 border-t border-[#eadfd3] pt-4 pl-4 text-[10px] font-bold uppercase tracking-[0.22em] text-[#b04600]">More</div>
                  <button type="button" onClick={() => goMobileRoute("/passport")} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    <Trophy size={18} /> Local Passport
                  </button>
                  <button type="button" onClick={() => goMobileRoute("/about")} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    <Store size={18} /> About Arctic Plate
                  </button>
                  <button type="button" onClick={() => goMobileRoute("/fees-delivery")} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    <Truck size={18} /> Fees &amp; delivery
                  </button>
                  <button type="button" onClick={() => goMobileRoute("/support")} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    <LifeBuoy size={18} /> Support
                  </button>
                  <button type="button" onClick={() => { setMobileMenuOpen(false); if (logout) logout(); navigate("/"); }} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    <LogOut size={18} /> Sign Out
                  </button>
                </>
              ) : (
                <>
                  <button type="button" onClick={() => goMobileRoute("/about")} className="w-full rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    About Arctic Plate
                  </button>
                  <button type="button" onClick={() => goMobileRoute("/fees-delivery")} className="w-full rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    Fees &amp; delivery
                  </button>
                  <button type="button" onClick={() => goMobileRoute("/support")} className="w-full rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    Support
                  </button>
                  <div className="my-3 border-t border-[#eadfd3] pt-4 pl-4 text-[10px] font-bold uppercase tracking-[0.22em] text-[#b04600]">Your Arctic Plate</div>
                  <button type="button" onClick={() => goMobileRoute("/sell-food")} className="w-full rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    Sell on Arctic Plate
                  </button>
                  <button type="button" onClick={() => goMobileRoute("/login")} className="w-full rounded-2xl px-4 py-3.5 text-left text-base font-semibold text-[#17212b] hover:bg-white">
                    Sign In
                  </button>
                </>
              )}

            </nav>
            <div className="border-t border-[#eadfd3] bg-[#fffaf4] p-4"><button type="button" onClick={() => goMobileRoute("/cart")} className="w-full rounded-xl bg-[#10233f] px-4 py-3 text-left text-sm font-bold text-white">
              Cart{count > 0 ? ` · ${count} item${count === 1 ? "" : "s"}` : ""}
            </button></div>
          </aside>
        </div>
      )}

      <ConnectionStatus />
      <InstallAppPrompt
        eligible={isMarketplaceHome && navRole !== "vendor" && navRole !== "admin"}
        hasBottomNav={showGlobalMobileNav}
      />

      {/* Reserve bottom space only where this shell actually renders a fixed
          mobile navigation. Vendor routes own their own bottom-nav clearance;
          desktop and anonymous pages should not carry dead vertical space. */}
      <main
        data-testid="layout-content"
        className={showGlobalMobileNav ? "pb-24 md:pb-0" : ""}
      >
        {children}
      </main>

      {showCustomerWelcome && navRole === "customer" && (
        <CustomerWelcomeCard
          user={user}
          onClose={() => {
            setShowCustomerWelcome(false);
            try { window.sessionStorage.removeItem(CUSTOMER_WELCOME_KEY); } catch {}
          }}
          onPassport={() => {
            setShowCustomerWelcome(false);
            try { window.sessionStorage.removeItem(CUSTOMER_WELCOME_KEY); } catch {}
            navigate("/passport");
          }}
          onBrowse={() => {
            setShowCustomerWelcome(false);
            try { window.sessionStorage.removeItem(CUSTOMER_WELCOME_KEY); } catch {}
            navigate("/");
          }}
        />
      )}

      {!isAdminPath && navRole !== "vendor" && footer.enabled && (
        <footer className="border-t border-[var(--ap-border)] bg-[var(--ap-surface)] px-5 py-8" data-testid="site-footer">
          <div className="mx-auto max-w-5xl">
            <div className="font-serif text-xl font-semibold text-[var(--ap-ink)]">{footer.heading || navSettings.brand_label || "Arctic Plate"}</div>
            {footer.support_text && <p className="mt-2 text-sm text-[var(--ap-ink-soft)]">{footer.support_text}</p>}
            <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-sm font-semibold">
              {(footer.links || []).map((item) => <Link key={`${item.route}-${item.label}`} to={item.route} className="text-[var(--ap-ink)] hover:text-[var(--ap-primary)]">{item.label}</Link>)}
              {(footer.social_links || []).map((item) => <a key={`${item.url}-${item.label}`} href={item.url} target="_blank" rel="noreferrer" className="text-[var(--ap-ink)] hover:text-[var(--ap-primary)]">{item.label}</a>)}
            </div>
            {footer.copyright_text && <div className="mt-5 text-xs text-[var(--ap-ink-soft)]">© {new Date().getFullYear()} {footer.copyright_text}</div>}
          </div>
        </footer>
      )}

      {/* Bottom nav — mobile only. Vendor routes render SellerBottomNav. */}
      {showGlobalMobileNav && (
        <nav
          data-testid="bottom-nav"
          className="md:hidden fixed bottom-0 inset-x-0 mx-auto w-full max-w-md bg-white border-t border-[var(--ap-border)] z-40"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        >
          <div className="flex justify-around py-2 px-2">
            {tabs.map((t) => (
              <NavLink
                key={t.to}
                to={t.to}
                data-testid={t.testid}
                end={t.to === "/"}
                aria-current={(location.pathname === t.to || t.activePaths?.includes(location.pathname)) ? "page" : undefined}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-0.5 py-2 px-3 min-w-[56px] transition-colors duration-150 ${
                    (isActive || t.activePaths?.includes(location.pathname)) ? "text-[var(--ap-primary)]" : "text-[var(--ap-ink-soft)]"
                  }`
                }
              >
                <div className="relative">
                  <t.icon size={20} strokeWidth={1.8} />
                  {t.badge > 0 && (
                    <span
                      data-testid={`${t.testid}-badge`}
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[var(--ap-primary)] text-white text-[10px] font-bold flex items-center justify-center"
                      aria-label={`${t.badge} unread`}
                    >
                      {t.badge > 99 ? "99+" : t.badge}
                    </span>
                  )}
                </div>
                <span className="text-[10px] tracking-wider uppercase font-medium">{t.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
}
